import { z } from "zod";
import { ChooseLanguage } from "./chooseLanguage";
import { EventInfo } from "./eventInfo";
import { EventSection } from "./eventSection";
import { InternalSetup } from "./internalSetup";
import { MultilingualContent } from "./multilingualContent";
import { TicketOnSale } from "./ticketOnSale";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useCreate, useParse, useUpdate } from "@refinedev/core";
import {
  CreateEventStep,
  FrequencyOfSellType,
  IAttractionBuildEventPageForm,
  IEventDetail,
  PriceType,
  SupportedCurrency,
  SupportedLanguage,
  TicketPlatform,
} from "interfaces";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import { CheckCircleIcon, MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { useEffect, useState } from "react";
import { REGEX_DOMAIN } from "utills/constants";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { get, isEmpty } from "lodash";
import { languageSchema } from "routes/event/schema";
import moment from "moment";
import { ConfirmModal } from "components/confirmModal";
import { SelectTicketingPlatform } from "./selectTicketingPlatform";

enum Section {
  LANGUAGES = "languages",
  MULTILINGUAL_CONTENT = "multilingual_content",
  EVENT_INFO = "event_info",
  DETAILS = "details",
  TICKET_ON_SALE = "ticket_on_sale",
  INTERNAL_SETUP = "internal_setup",
  NONE = "none",
}

const BuildEventPageSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    languages: z.array(z.string()).min(1, "This field is required"),
    platforms: z
      .array(z.string())
      .min(1, "This field is required"),
    multilingual_content: z.object({
      name: languageSchema(languages),
      place: languageSchema(languages),
      merchant_name: z.object({
        en: z.string().optional().nullable(),
        vi: z.string().optional().nullable(),
      })
    }),
    event_info: z
      .object({
        banner_url: z.string().nullable().optional(),
        currency: z.string().min(1, "This field is required"),
        timezone: z.string().min(1, "This field is required"),
        start_date: z.string().min(1, "This field is required"),
        end_date: z.string().min(1, "This field is required"),
        country: z.string().min(1, "This field is required"),
        venue: z.string().optional(),
        event_slug: z
          .string()
          .min(1, "This field is required")
          .refine((value) => REGEX_DOMAIN.test(value ?? ""), "Invalid slug"),
        event_category_ids: z.array(z.string()),
        special_type: z.string().optional(),
        frequency_of_sell: z.string().min(1, "This field is required"),
        other_event_dates: z.array(z.string()),
          date_pricing: z.object({
            enabled: z.boolean().default(false),
            holidays: z.array(
              z.object({
                date: z.string(),
                name: z.string(),
              })
            ).default([]),
          }).default({ enabled: false, holidays: [] }),
      })
      .refine((data) => moment(data.start_date).isSameOrBefore(moment(data.end_date)), {
        message: "End date cannot be earlier than start date.",
        path: ["end_date"],
      }).superRefine(({ country, venue }, ctx) => {
        if (country === "Vietnam" && isEmpty(venue)) {
          ctx.addIssue({
            path: ["venue"],
            code: "custom",
            message: "This field is required",
          });
        }
      }),
    ticket_on_sale: z
      .object({
        open_registration_date: z.string().min(1, "This field is required"),
        close_registration_date: z.string().min(1, "This field is required"),
      })
      .refine(
        (data) => moment(data.open_registration_date).isSameOrBefore(moment(data.close_registration_date)),
        {
          message: "Close date cannot be earlier than open date.",
          path: ["close_registration_date"],
        }
      ),
    internal_setup: z.object({
      merchant_id: z.string().min(1, "This field is required"),
      apply_for_partners: z.array(z.string()).optional(),
      selling_max_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "Quantity is required",
        })
        .min(0, {message:"Quantity must be equal or greater than 0"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      min_price: z
        .number({
          invalid_type_error: "Min Price must be a number",
          required_error: "Min Price is required",
        })
        .min(0, {message:"Min Price must be equal or greater than 0"})
        .nonnegative({ message: "Min Price must be a positive number" }),
      price_type: z.string().min(1, "This field is required"),
    }),
    details: z.array(
      z.object({
        title: languageSchema(languages),
        description: languageSchema(languages, true),
      })
    ),
  });
};

const getDefaultValues = (defaultValues: IEventDetail | undefined) => {
  return {
    languages: defaultValues?.languages ?? [SupportedLanguage.EN],
    platforms:
      defaultValues?.platforms === null
        ? []
        : defaultValues?.platforms ?? [TicketPlatform.ACTIUP],
    multilingual_content: {
      name: {
        en: defaultValues?.name?.en ??  "",
        vi: defaultValues?.name?.vi ??  "",
      },
      place: {
        en: defaultValues?.place?.en ?? "",
        vi: defaultValues?.place?.vi ?? "",
      },
      merchant_name: {
        en: defaultValues?.merchant_name?.en ?? "",
        vi: defaultValues?.merchant_name?.vi ?? "",
      }
    },
    details: defaultValues?.details ?? [],
    event_info: {
      banner_url: defaultValues?.banner_url ?? "",
      currency: defaultValues?.currency ?? SupportedCurrency.VND,
      timezone: defaultValues?.timezone ?? "Asia/Ho_Chi_Minh",
      start_date: defaultValues?.start_date ?? "",
      end_date: defaultValues?.end_date ?? "",
      country: defaultValues?.country ?? "Vietnam",
      event_category_ids: defaultValues?.event_category_ids ?? [],
      event_slug: defaultValues?.event_slug ?? "",
      special_type: defaultValues?.special_type ?? "",
      venue: defaultValues?.venue ?? "",
      frequency_of_sell: defaultValues?.frequency_of_sell ?? FrequencyOfSellType.ONCE,
      other_event_dates: defaultValues?.other_event_dates ?? [],
      date_pricing: 
        defaultValues?.frequency_of_sell === FrequencyOfSellType.USUALLY
          ? (defaultValues?.date_pricing ?? { enabled: false, holidays: [] })
          : undefined,
    },
    ticket_on_sale: {
      open_registration_date: defaultValues?.open_registration_date ?? "",
      close_registration_date: defaultValues?.close_registration_date ?? "",
    },
    internal_setup: {
      apply_for_partners: defaultValues?.apply_for_partners ?? [],
      merchant_id: defaultValues?.merchant_id ?? "",
      selling_max_qty: defaultValues?.selling_max_qty ?? 0,
      min_price: defaultValues?.min_price ?? 0,
      price_type: defaultValues?.price_type ?? PriceType.PAID
    },
  }
}

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  content: "",
  submit: () => {}
}

export const BuildEventPageStep = (props: {eventDetailData?: IEventDetail}) => {
  const { eventDetailData } = props;
  const parse = useParse();
  const { params } = parse();

  if (params?.id) {
    return eventDetailData ? <BuildEventPageStepForm {...props} /> : <></>;
  }
  return <BuildEventPageStepForm {...props} />
}

export const BuildEventPageStepForm = ({eventDetailData}: {eventDetailData?: IEventDetail, schema?: any}) => {
  const { mutate, isLoading: isLoading } = useCreate();
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const parse = useParse();
  const { params } = parse();

  const navigate = useNavigate();

  const [openSectionId, setOpenSectionId] = useState<Section>(Section.NONE);
  const [confirmModalConfig, setConfirmModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const methods = useForm<IAttractionBuildEventPageForm, HttpError, IAttractionBuildEventPageForm>({
    defaultValues: getDefaultValues(eventDetailData),
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        BuildEventPageSchema(values.languages)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    methods.reset(getDefaultValues(eventDetailData));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetailData])

  const onSubmit = (data: IAttractionBuildEventPageForm) => {
    const submitValues = {
      details: data.details,
      languages: data.languages,
      platforms: data.platforms,
      ...data.multilingual_content,
      ...data.event_info,
      ...data.ticket_on_sale,
      ...data.internal_setup,
      current_step: 1,
    };

    if (eventDetailData?.id) {
      if ((eventDetailData.current_step >= CreateEventStep.CREATE_TICKET_PHASE) && ((submitValues.frequency_of_sell !== eventDetailData.frequency_of_sell) || (submitValues.currency !== eventDetailData.currency))) {
        let title = "";
        let content = "";
        if ((submitValues.frequency_of_sell !== eventDetailData.frequency_of_sell) && (submitValues.currency !== eventDetailData.currency)) {
          title = "Are you sure you want to proceed with this change?";
          content = "All existing tickets will be deleted when you change the Event Type.\nAll currency fields will be automatically recalculated and converted based on the current exchange rate when you change the currency unit. Payment methods will also adjust accordingly.";
        } else if (submitValues.currency !== eventDetailData.currency) {
          title = "Are you sure you want to change the currency unit?";
          content = "All currency fields will be automatically recalculated and converted based on the current exchange rate when you change the currency unit. Payment methods will also change according to the currency unit you have selected.";
        } else {
          title = "Do you want to change the Event Type?";
          content = eventDetailData.frequency_of_sell === FrequencyOfSellType.SEATMAP ? "All available seat layouts and tickets will be deleted when you change the event type." : "All available tickets will be deleted when you change the event type.";
        }
        setConfirmModalConfig({
          open: true,
          title: title,
          content: content,
          submit: () => _updateEvent(submitValues, eventDetailData.id)
        });
      } else {
        _updateEvent(submitValues, eventDetailData.id);
      }
    } else {
      _createEvent(submitValues);
    }
  };
  const _createEvent = (submitValues: any) => {
    mutate(
      {
        resource: "content/events/attractions",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Create successfully");
          navigate(
            `/events/attractions/${get(data, "data.data.id", "")}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`
          );
        },
      }
    );
  }

  const _updateEvent = (submitValues: any, id: string) => {
    updateMutation(
      {
        resource: "content/events/attractions",
        id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Update successfully");
          navigate(
            `/events/attractions/${get(data, "data.data.id", "")}/edit?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`
          );
        },
      }
    );
  }

  const onError = (errors: any) => {
    methods.trigger(openSectionId, { shouldFocus: true }).then((isValid) => {
      if (isValid) {
        const firstError = getFirstError(errors) as keyof IAttractionBuildEventPageForm;
        if (firstError) {
          methods.trigger(firstError).then(() => {
            setOpenSectionId(firstError as Section);
          });
        }
      }
    });
  };

  const getFirstError = (errors: any) => {
    const firstError = (
      Object.keys(errors) as Array<keyof typeof errors>
    ).reduce<keyof typeof errors | null>((field, a) => {
      const fieldKey = field as keyof typeof errors;
      return !!errors[fieldKey] ? fieldKey : a;
    }, null);
    return firstError;
  };

  const _renderCheckMark = (key: keyof IAttractionBuildEventPageForm) => {
    return (!methods.formState.errors[key] 
    && ((key === "languages" || params?.id) ? true : methods.getFieldState(key).isDirty)
    && openSectionId !== key) ? <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" /> : <></>;
  }

  const _handleSubmitSection = (sectionId: Section) => {
    if (!openSectionId) return setOpenSectionId(sectionId);
    methods.trigger(openSectionId).then((isValid) => {
      if (isValid)
        setOpenSectionId(
          openSectionId === sectionId ? Section.NONE : sectionId
        );
    });
  };

  const _onCloseChangeEventTypeModal = () => {
    setConfirmModalConfig({...INITIAL_ACTION_MODAL_CONFIG, title: confirmModalConfig.title, content: confirmModalConfig.content});
  }

  console.log(methods.formState.errors);
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
        <div className="flex flex-col gap-y-4 sm:gap-y-6">
          <SelectTicketingPlatform />
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Choose Event Language
              </h3>
              {_renderCheckMark("languages")}
              <button
                onClick={() => _handleSubmitSection(Section.LANGUAGES)}
                type="button"
              >
                {openSectionId === Section.LANGUAGES ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <ChooseLanguage isEdit={openSectionId === Section.LANGUAGES} />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Multilingual Content
              </h3>
              {_renderCheckMark("multilingual_content")}
              <button
                onClick={() =>
                  _handleSubmitSection(Section.MULTILINGUAL_CONTENT)
                }
                type="button"
              >
                {openSectionId === Section.MULTILINGUAL_CONTENT ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <MultilingualContent
              isEdit={openSectionId === Section.MULTILINGUAL_CONTENT}
              id={params?.id}
            />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 flex-row sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Add Event Info
              </h3>
              {_renderCheckMark("event_info")}
              <button
                onClick={() => _handleSubmitSection(Section.EVENT_INFO)}
                type="button"
              >
                {openSectionId === Section.EVENT_INFO ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <EventInfo isEdit={openSectionId === Section.EVENT_INFO} id={params?.id} eventDetailData={eventDetailData ?? null} />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-row flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Add more sections to your event page
              </h3>
              {_renderCheckMark("details")}
              <button
                onClick={() => _handleSubmitSection(Section.DETAILS)}
                type="button"
              >
                {openSectionId === Section.DETAILS ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <EventSection isEdit={openSectionId === Section.DETAILS} id={params?.id} />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-row flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Ticket On Sale
              </h3>
              {_renderCheckMark("ticket_on_sale")}
              <button
                onClick={() => _handleSubmitSection(Section.TICKET_ON_SALE)}
                type="button"
              >
                {openSectionId === Section.TICKET_ON_SALE ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <TicketOnSale isEdit={openSectionId === Section.TICKET_ON_SALE} id={params?.id} />
          </div>
          <div className="box-container p-0">
            <div className="flex flex-row flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:items-center sm:justify-between">
              <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
                Internal Setup
              </h3>
              {_renderCheckMark("internal_setup")}
              <button
                onClick={() => _handleSubmitSection(Section.INTERNAL_SETUP)}
                type="button"
              >
                {openSectionId === Section.INTERNAL_SETUP ? (
                  <MinusCircleIcon className="w-6 h-6 text-brand-500" />
                ) : (
                  <PlusCircleIcon className="w-6 h-6 text-brand-500" />
                )}
              </button>
            </div>
            <InternalSetup isEdit={openSectionId === Section.INTERNAL_SETUP} id={params?.id} />
          </div>
          <div
            className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
            style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
          >
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/events/attractions`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-fit" type="submit">
                Save & Next
              </Button>
            </div>
          </div>
        </div>
      </form>
      {(isLoading || updateMutationIsLoading) ? <FullScreenLoading /> : <></>}
      <ConfirmModal 
        key="confirm-change-field"
        title={confirmModalConfig.title}
        content={confirmModalConfig.content}
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseChangeEventTypeModal}
      />
    </FormProvider>
  );
};
